import { flatten } from 'lodash';
import memoize from 'fast-memoize';

import { MenuItem, MenuItems } from './Menu';
import {
  MAX_WIDTH,
  MIN_WIDTH,
  ROW_FONT_SIZE,
  ROW_SIDES_PADDING,
  ICON_MARGIN,
  ROW_HEIGHT,
  CONTAINER_VERTICAL_PADDING,
  MARGIN_TO_ROOT,
} from './styles';

export type MenuDirection =
  | 'above-right'
  | 'above-left'
  | 'below-right'
  | 'below-left'
  | 'side-right-up'
  | 'side-right-down'
  | 'side-left-up'
  | 'side-left-down';
const directionIsRight = (direction: MenuDirection) => direction.includes('right');

const getRowWidth = (menuItem: MenuItem) => {
  const { text, Icon } = menuItem;
  /* 
    attach dummy element to DOM to query its
    rendered width. We have to do this because
    rendered text isn't predictable based on 
    font size 
   */
  const dummyRow = document.createElement('div');
  dummyRow.style.maxWidth = `${MAX_WIDTH}px`;
  dummyRow.style.minWidth = `${MIN_WIDTH}px`;
  dummyRow.style.fontSize = `${ROW_FONT_SIZE}px`;
  dummyRow.style.padding = `0px ${ROW_SIDES_PADDING}px`;
  dummyRow.innerText = text;

  /* 
    make sure div is invisible and off screen
    technically this code is running synchronously so
    the element should be removed before the browser has a change
    to paint, but just in case let's make sure it doesn't show up. 
    immediately remove it when done getting width
  */
  dummyRow.style.visibility = 'hidden';
  dummyRow.style.position = 'fixed';
  dummyRow.style.left = '-1000px';
  dummyRow.style.top = '-1000px';
  document.body.appendChild(dummyRow);
  const renderedWidth = dummyRow.clientWidth;
  document.body.removeChild(dummyRow);

  // add in space for the icon as if it were also rendered
  const iconSectionWidth = Icon ? ICON_MARGIN + 16 : 0;
  return iconSectionWidth + renderedWidth;
};

const getMaxWidth = (menuItems: MenuItems): number => {
  const menuItems_ = flatten(menuItems);
  const maxWidth = menuItems_.reduce((maxSoFar, menuItem) => Math.max(maxSoFar, getRowWidth(menuItem)), 0);
  const filteredItems = menuItems_.filter((item) => item.subMenu !== undefined);
  if (filteredItems.length === 0) {
    return maxWidth;
  }
  // submenu will never be undefined
  const widths = filteredItems.map((item) => maxWidth + (item.subMenu === undefined ? 0 : getMaxWidth(item.subMenu)));
  return widths.reduce((previousMax, currentWidth) => Math.max(previousMax, currentWidth), 0);
};

const getMenuHeight = (menuItemsCount: number, isSubmenu: boolean): number =>
  menuItemsCount * ROW_HEIGHT + (!isSubmenu ? 2 : 1) * CONTAINER_VERTICAL_PADDING; // submenus are offset vertically by half of container padding so their first item lines up with the parent item

export const getMenuDirection = memoize(
  (
    menuItems: MenuItems,
    top: number,
    bottom: number,
    left: number,
    right: number,
    isSubMenu: boolean,
    initialDirection: MenuDirection = 'below-right',
  ): MenuDirection => {
    const verticalDirectionOffset = MARGIN_TO_ROOT;
    const paddingToEdgeOfScreen = 16;

    const rowOffset = isSubMenu ? -ROW_HEIGHT : 0;
    const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
    const roomToBottom = vh - bottom - verticalDirectionOffset;
    const roomToTop = top - verticalDirectionOffset;
    const menuHeight = getMenuHeight(flatten(menuItems).length, isSubMenu);
    const hasRoomBelow = menuHeight + paddingToEdgeOfScreen + rowOffset < roomToBottom;

    /*
    continue extending in the direction of the root-most menu
    prefer going down, otherwise choose the direction that would
    clip the least
  */
    if (isSubMenu) {
      if (directionIsRight(initialDirection))
        return hasRoomBelow || roomToBottom >= roomToTop ? 'side-right-down' : 'side-right-up';
      if (!directionIsRight(initialDirection))
        return hasRoomBelow || roomToBottom >= roomToTop ? 'side-left-down' : 'side-left-up';
    }

    // don't run width calculations for submenus
    const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    const roomToRight = vw - left;
    const roomToLeft = right;
    const maxWidth = getMaxWidth(menuItems);
    const hasRoomToRight = maxWidth + paddingToEdgeOfScreen < roomToRight;
    const hasRoomToLeft = maxWidth + paddingToEdgeOfScreen < roomToLeft;

    /* 
    order of precedence:
      below-right
      below-left
      above-right
      above-left
    
    if there's no room either horizontally or vertically, choose the direction
    that will clip the least amount of content
  */
    if (hasRoomToRight || roomToRight >= roomToLeft) {
      if (hasRoomBelow || roomToBottom >= roomToTop) return 'below-right';
      return isSubMenu ? 'side-right-up' : 'above-right';
    }
    if (hasRoomToLeft || roomToLeft >= roomToRight) {
      if (hasRoomBelow || roomToBottom >= roomToTop) return 'below-left';
      return 'above-left';
    }

    // should be unreachable
    return 'below-right';
  },
);
